import moment from "moment";
import { formatDate } from "../../../../../utils/formatDate";

const REMARK_UPDATED_AT_FORMAT = "MM/DD/YYYY HH:mm";

export type AgenticAuthOnFileRemarkEntry = {
  user: string;
  updatedAt: string;
  remark: string;
};

/** Multiple threads per row; each thread is ordered history. */
export type AgenticAuthOnFileRemarks = AgenticAuthOnFileRemarkEntry[][];

const remarkTimeMs = (e: AgenticAuthOnFileRemarkEntry): number => {
  if (!e.updatedAt || e.updatedAt === "—") return Number.NEGATIVE_INFINITY;
  const strict = moment(
    e.updatedAt,
    [REMARK_UPDATED_AT_FORMAT, moment.ISO_8601],
    true,
  );
  const m = strict.isValid() ? strict : moment(e.updatedAt);
  return m.isValid() ? m.valueOf() : Number.NEGATIVE_INFINITY;
};

/** Newest non-placeholder remark text for the table (time desc; ties → later in list). */
export const formatAgenticAuthOnFileRemarksForTable = (
  remarks: AgenticAuthOnFileRemarks,
): string => {
  const entries = remarks.flat().filter(Boolean);
  if (!entries.length) return "—";

  const ranked = entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const byTime = remarkTimeMs(b.e) - remarkTimeMs(a.e);
      return byTime !== 0 ? byTime : b.i - a.i;
    });

  return ranked.find((x) => x.e.remark && x.e.remark !== "NA")?.e.remark ?? "—";
};

/** Primary thread: replace NA placeholder or append. */
export const appendAgenticAuthOnFileRemark = (
  remarks: AgenticAuthOnFileRemarks,
  entry: AgenticAuthOnFileRemarkEntry,
): AgenticAuthOnFileRemarks => {
  const copy = structuredClone(remarks) as AgenticAuthOnFileRemarks;
  if (!copy.length) return [[entry]];
  const thread = copy[0];
  const onlyPlaceholder = thread.length === 1 && thread[0].remark === "NA";
  copy[0] = onlyPlaceholder ? [entry] : [...thread, entry];
  return copy;
};

export const createAgenticAuthOnFileRemarkEntry = (
  user: string,
  remarkText: string,
): AgenticAuthOnFileRemarkEntry => ({
  user: user || "—",
  updatedAt: formatDate(new Date(), REMARK_UPDATED_AT_FORMAT, ""),
  remark: remarkText,
});

/** Flat array for Firestore (no nested arrays). */
export type AgenticAuthOnFileRemarkFirestoreEntry = {
  user: string;
  remark: string;
  updated_at: string;
};

export const serializeAgenticAuthRemarksForFirestore = (
  remarks: AgenticAuthOnFileRemarks,
): AgenticAuthOnFileRemarkFirestoreEntry[] =>
  remarks.flat().map((e) => ({
    user: String(e.user ?? "—"),
    remark: String(e.remark ?? ""),
    updated_at: String(e.updatedAt ?? ""),
  }));
